// ── Guidance content registry ────────────────────────────────────────────────
// Single source of truth for in-app guidance. Add a guide here (plus a
// data-guide / data-tour anchor on the target element for coachmarks) and the
// engine takes care of the rest: when to auto-show it, skip/replay handling,
// and listing it in the Help launcher.
//
// Guide shape:
//   id            Stable key used for progress persistence — DO NOT rename
//                 once shipped (renaming resets every user's progress).
//   type          "tour"  — multi-step product walkthrough
//                 "coachmark" — short, page-scoped tip(s)
//   title         Shown in the Help launcher list.
//   description   One-liner under the title in the Help launcher.
//   trigger       How it auto-shows:
//                   { on: "firstLogin" }                     once, shortly after login
//                   { on: "route", path, firstVisitOnly }    when a route is opened
//                   { on: "manual" }                         only from the Help launcher
//   steps         react-joyride steps: { target, title, content, placement, disableBeacon }
//                 `target` is a CSS selector — prefer a stable data-* attribute.

export const GUIDES = [
  {
    id: "core-tour",
    type: "tour",
    title: "Product tour",
    description: "A guided walkthrough of every part of the workspace.",
    trigger: { on: "firstLogin" },
    steps: [
      {
        target: '[data-tour="nav-home"]',
        title: "Your home base",
        content:
          "This is Home — your dashboard with KPIs, the hiring funnel, recent activity, and quick actions. You're looking at it now.",
        placement: "right",
      },
      {
        target: '[data-tour="lifecycle-nav"]',
        title: "The hiring lifecycle",
        content:
          "The heart of Ample Hire. Work flows left → right through five stages: Plan → Source → Interview → Compare → Hire. Click any stage to jump in.",
        placement: "bottom",
      },
      {
        target: '[data-tour="stage-plan"]',
        title: "1 · Plan",
        content:
          "Create job openings, generate job descriptions with AI, build your question bank, and configure the screening rounds for each role.",
        placement: "bottom",
      },
      {
        target: '[data-tour="stage-source"]',
        title: "2 · Source",
        content:
          "Build talent pools, upload and parse resumes, and invite candidates to apply or interview.",
        placement: "bottom",
      },
      {
        target: '[data-tour="stage-interview"]',
        title: "3 · Interview",
        content:
          "Track interview sessions as they progress and review detailed, AI-assisted candidate reports.",
        placement: "bottom",
      },
      {
        target: '[data-tour="stage-compare"]',
        title: "4 · Compare",
        content:
          "Put your shortlisted candidates side-by-side — compare scores, answers, and skills to make a confident decision.",
        placement: "bottom",
      },
      {
        target: '[data-tour="stage-hire"]',
        title: "5 · Hire",
        content:
          "Move candidates through your pipeline, shortlist the best, send offers, and track who's been hired.",
        placement: "bottom",
      },
      {
        target: '[data-tour="job-scope"]',
        title: "Scope your work",
        content:
          "Pick which jobs to focus on. Everything in the lifecycle filters to your selection, so you only see what's relevant.",
        placement: "bottom",
      },
      {
        target: '[data-tour="nav-live"]',
        title: "Live interviews",
        content:
          "Monitor interviews happening right now in real time.",
        placement: "right",
      },
      {
        target: '[data-tour="nav-analytics"]',
        title: "Analytics",
        content:
          "Dashboards and reports on funnel health, interview throughput, and team performance.",
        placement: "right",
      },
      {
        target: '[data-tour="ai-assistant"]',
        title: "Stuck? Ask the AI",
        content:
          "The assistant answers questions about candidates, jobs, and how to use the platform.",
        placement: "right",
      },
      {
        target: '[data-tour="nav-team"]',
        title: "Your team",
        content:
          "Invite colleagues, assign roles, and manage permissions so hiring is a team effort.",
        placement: "right",
      },
      {
        target: '[data-tour="more-nav"]',
        title: "Everything else lives in More",
        content:
          "Click More for Setup, Integrations, Scheduling, Communications, Search (candidates & interviews), Audit log, Exports, and Email templates.",
        placement: "right",
      },
      {
        target: '[data-tour="setup-checklist"]',
        title: "Steps to your first hire",
        content:
          "Finish these to make this workspace yours. Sample data is included so you can explore — replace it when you're ready. This panel hides itself once you're done.",
        placement: "top",
      },
      {
        target: '[data-tour="help-launcher"]',
        title: "That's the tour!",
        content:
          "Replay this tour — or start any other guide — anytime from here. You can also turn automatic tips off.",
        placement: "right",
      },
    ],
  },
  {
    id: "jobs-intro",
    type: "coachmark",
    title: "Creating jobs",
    description: "Where to add and manage roles.",
    trigger: { on: "route", path: "/admin/jobs", firstVisitOnly: true },
    steps: [
      {
        target: '[data-guide="jobs-create"]',
        title: "Create a job",
        content:
          "Start here to add a new role — define the requirements and build the screening flow. You can also generate one with AI.",
        placement: "bottom",
        disableBeacon: true,
      },
    ],
  },
  {
    id: "whatsapp-setup",
    type: "coachmark",
    title: "Set up WhatsApp",
    description: "Connect WhatsApp Business to message candidates.",
    trigger: { on: "route", path: "/admin/whatsapp-settings", firstVisitOnly: true },
    steps: [
      {
        target: '[data-guide="whatsapp-config"]',
        title: "Connect WhatsApp",
        content:
          "Enter your Meta phone number ID, access token, and other credentials here to send and receive WhatsApp messages with candidates.",
        placement: "bottom",
      },
    ],
  },
  {
    id: "email-setup",
    type: "coachmark",
    title: "Set up email",
    description: "Configure how Ample Hire sends candidate emails.",
    trigger: { on: "route", path: "/admin/email-settings", firstVisitOnly: true },
    steps: [
      {
        target: '[data-guide="email-config"]',
        title: "Connect email",
        content:
          "Choose a provider (AWS SES or SMTP) and add your credentials so invitations, reminders, and notifications reach candidates.",
        placement: "bottom",
      },
    ],
  },
];

export const getGuide = (id) => GUIDES.find((g) => g.id === id) || null;
